/**
 * Reusable "New Ticket…" capture: viewport → one highlight rect → POST.
 * No capture libraries; SVG foreignObject + canvas of the current page.
 */
import { authenticatedFetch } from "./dashboard-auth.js";
import { escapeHtml } from "./dashboard-html.js";

export const NEW_TICKET_LABEL = "New Ticket…";
export const NEW_TICKET_PATH = "/dashboard/tickets";
export const NEW_TICKET_ACCENT = "#0f4c3a";

export function newTicketButtonHtml(): string {
  return `<button type="button" class="btn" data-new-ticket>${escapeHtml(NEW_TICKET_LABEL)}</button>`;
}

export function newTicketOverlayHtml(): string {
  return `
    <div class="new-ticket-overlay" role="dialog" aria-modal="true" aria-labelledby="new-ticket-title" data-new-ticket-overlay>
      <div class="new-ticket-chrome">
        <div>
          <p id="new-ticket-title">${escapeHtml(NEW_TICKET_LABEL)}</p>
          <p class="muted" id="new-ticket-help" data-new-ticket-help>Draw one rectangle to highlight, or use the full window. Esc cancels.</p>
        </div>
        <div class="new-ticket-chrome-actions">
          <button type="button" class="btn" data-new-ticket-full>Use full window</button>
          <button type="button" class="btn" data-new-ticket-cancel>Cancel</button>
        </div>
      </div>
      <div class="new-ticket-draw" data-new-ticket-draw>
        <canvas data-new-ticket-canvas tabindex="0" aria-label="Captured window. Draw a rectangle, or press Enter for the full window."></canvas>
        <div class="new-ticket-marquee" data-new-ticket-marquee hidden></div>
      </div>
      <form class="new-ticket-form" data-new-ticket-form hidden>
        <label for="new-ticket-text">Bug or feature</label>
        <textarea id="new-ticket-text" name="text" required maxlength="8000"></textarea>
        <div class="new-ticket-form-actions">
          <button type="submit" class="btn main">Submit</button>
          <button type="button" class="btn" data-new-ticket-cancel>Cancel</button>
        </div>
        <p class="muted" data-new-ticket-status role="status"></p>
      </form>
    </div>
  `;
}

export type HighlightRect = { x: number; y: number; width: number; height: number };

export function normalizeHighlight(
  start: { x: number; y: number },
  end: { x: number; y: number },
  bounds: { width: number; height: number }
): HighlightRect {
  const x1 = clamp(Math.min(start.x, end.x), 0, bounds.width);
  const y1 = clamp(Math.min(start.y, end.y), 0, bounds.height);
  const x2 = clamp(Math.max(start.x, end.x), 0, bounds.width);
  const y2 = clamp(Math.max(start.y, end.y), 0, bounds.height);
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

export function highlightIsUsable(rect: HighlightRect): boolean {
  return rect.width >= 8 && rect.height >= 8;
}

export function ticketSubmitBody(input: {
  text: string;
  pageUrl: string;
  image: string;
  sp?: string | null;
}): { text: string; pageUrl: string; image: string; sp?: string } {
  const body: { text: string; pageUrl: string; image: string; sp?: string } = {
    text: input.text.trim(),
    pageUrl: input.pageUrl,
    image: input.image
  };
  const sp = input.sp?.trim();
  if (sp) body.sp = sp;
  return body;
}

export function bindNewTicketControl(root: ParentNode, options: { sp?: string | null } = {}): void {
  root.querySelectorAll<HTMLButtonElement>("[data-new-ticket]").forEach((button) => {
    button.addEventListener("click", () => {
      void openNewTicketFlow({ sp: options.sp ?? null });
    });
  });
}

async function openNewTicketFlow(options: { sp: string | null }): Promise<void> {
  if (document.querySelector("[data-new-ticket-overlay]")) return;
  let snapshot: string;
  try {
    snapshot = await captureViewportPng();
  } catch {
    window.alert("Could not capture this page.");
    return;
  }
  const wrap = document.createElement("div");
  wrap.innerHTML = newTicketOverlayHtml().trim();
  const overlay = wrap.firstElementChild as HTMLElement;
  document.body.appendChild(overlay);

  const canvas = overlay.querySelector<HTMLCanvasElement>("[data-new-ticket-canvas]");
  const marquee = overlay.querySelector<HTMLElement>("[data-new-ticket-marquee]");
  const form = overlay.querySelector<HTMLFormElement>("[data-new-ticket-form]");
  const textarea = overlay.querySelector<HTMLTextAreaElement>("[name=text]");
  const status = overlay.querySelector<HTMLElement>("[data-new-ticket-status]");
  const help = overlay.querySelector<HTMLElement>("[data-new-ticket-help]");
  const fullBtn = overlay.querySelector<HTMLButtonElement>("[data-new-ticket-full]");
  if (!canvas || !marquee || !form || !textarea || !status || !help || !fullBtn) {
    overlay.remove();
    return;
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    overlay.remove();
    return;
  }
  const view = { canvas, ctx, marquee, form, textarea, status, help, fullBtn };
  const image = await loadImage(snapshot);
  view.canvas.width = image.naturalWidth;
  view.canvas.height = image.naturalHeight;
  view.ctx.drawImage(image, 0, 0);
  view.canvas.focus();

  let phase: "draw" | "compose" = "draw";
  let drag: { x: number; y: number } | null = null;
  let highlighted = snapshot;

  const close = (): void => {
    overlay.remove();
  };

  const applyHighlight = (rect: HighlightRect): void => {
    if (phase !== "draw") return;
    phase = "compose";
    view.ctx.drawImage(image, 0, 0);
    strokeHighlight(view.ctx, rect);
    highlighted = view.canvas.toDataURL("image/png");
    view.marquee.hidden = true;
    view.fullBtn.hidden = true;
    view.form.hidden = false;
    view.help.textContent = "Describe the bug or feature, then submit. Esc cancels.";
    view.textarea.focus();
  };

  overlay.querySelectorAll("[data-new-ticket-cancel]").forEach((node) => {
    node.addEventListener("click", close);
  });
  overlay.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (phase === "draw" && event.key === "Enter" && event.target !== view.textarea) {
      event.preventDefault();
      applyHighlight(fullWindowRect(view.canvas));
    }
  });
  view.fullBtn.addEventListener("click", () => applyHighlight(fullWindowRect(view.canvas)));

  view.canvas.addEventListener("pointerdown", (event) => {
    if (phase !== "draw") return;
    view.canvas.setPointerCapture(event.pointerId);
    drag = canvasPoint(view.canvas, event);
    view.marquee.hidden = false;
    placeMarquee(view.marquee, view.canvas, drag, drag);
  });
  view.canvas.addEventListener("pointermove", (event) => {
    if (!drag || phase !== "draw") return;
    placeMarquee(view.marquee, view.canvas, drag, canvasPoint(view.canvas, event));
  });
  view.canvas.addEventListener("pointerup", (event) => {
    if (!drag || phase !== "draw") return;
    const end = canvasPoint(view.canvas, event);
    const rect = normalizeHighlight(drag, end, { width: view.canvas.width, height: view.canvas.height });
    drag = null;
    view.marquee.hidden = true;
    applyHighlight(highlightIsUsable(rect) ? rect : fullWindowRect(view.canvas));
  });

  view.form.addEventListener("submit", (event) => {
    event.preventDefault();
    const text = view.textarea.value.trim();
    if (!text) {
      view.textarea.focus();
      return;
    }
    view.status.textContent = "Saving…";
    const payload = ticketSubmitBody({
      text,
      pageUrl: window.location.href,
      image: highlighted,
      sp: options.sp
    });
    void authenticatedFetch(NEW_TICKET_PATH, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    }).then(async (response) => {
      const body = await response.json().catch(() => ({})) as {
        ok?: boolean;
        id?: string;
        linear?: { identifier?: string; url?: string } | null;
        linearError?: string | null;
        error?: string;
      };
      if (!response.ok || !body.ok || !body.id) {
        view.status.textContent = body.error ? `Save failed (${body.error}).` : "Save failed.";
        return;
      }
      if (body.linear?.identifier) view.status.textContent = `Saved ${body.linear.identifier}.`;
      else if (body.linearError) view.status.textContent = `Saved ${body.id}. Linear not created.`;
      else view.status.textContent = `Saved ${body.id}. Linear not configured.`;
      window.setTimeout(close, 1600);
    }).catch(() => {
      view.status.textContent = "Save failed.";
    });
  });
}

export function strokeHighlight(ctx: CanvasRenderingContext2D, rect: HighlightRect): void {
  const width = Math.max(2, Math.round(Math.min(ctx.canvas.width, ctx.canvas.height) * 0.004) + 2);
  ctx.save();
  ctx.strokeStyle = NEW_TICKET_ACCENT;
  ctx.lineWidth = width;
  ctx.strokeRect(
    rect.x + width / 2,
    rect.y + width / 2,
    Math.max(0, rect.width - width),
    Math.max(0, rect.height - width)
  );
  ctx.restore();
}

function fullWindowRect(canvas: HTMLCanvasElement): HighlightRect {
  const inset = Math.max(4, Math.round(Math.min(canvas.width, canvas.height) * 0.01));
  return {
    x: inset,
    y: inset,
    width: Math.max(8, canvas.width - inset * 2),
    height: Math.max(8, canvas.height - inset * 2)
  };
}

function canvasPoint(canvas: HTMLCanvasElement, event: PointerEvent): { x: number; y: number } {
  const box = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - box.left) / box.width) * canvas.width,
    y: ((event.clientY - box.top) / box.height) * canvas.height
  };
}

function placeMarquee(
  marquee: HTMLElement,
  canvas: HTMLCanvasElement,
  start: { x: number; y: number },
  end: { x: number; y: number }
): void {
  const box = canvas.getBoundingClientRect();
  const host = canvas.parentElement?.getBoundingClientRect();
  if (!host) return;
  const rect = normalizeHighlight(start, end, { width: canvas.width, height: canvas.height });
  const left = box.left - host.left + (rect.x / canvas.width) * box.width;
  const top = box.top - host.top + (rect.y / canvas.height) * box.height;
  marquee.style.left = `${left}px`;
  marquee.style.top = `${top}px`;
  marquee.style.width = `${(rect.width / canvas.width) * box.width}px`;
  marquee.style.height = `${(rect.height / canvas.height) * box.height}px`;
}

async function captureViewportPng(): Promise<string> {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const clone = document.body.cloneNode(true) as HTMLElement;
  copyTree(document.body, clone);
  clone.querySelectorAll("script, style, link, noscript, iframe, [data-new-ticket-overlay]").forEach((node) => node.remove());
  clone.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
  clone.style.margin = "0";
  clone.style.transform = `translate(${-window.scrollX}px, ${-window.scrollY}px)`;
  const wrapper = document.createElement("div");
  wrapper.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
  wrapper.style.cssText = `width:${width}px;height:${height}px;overflow:hidden;background:${getComputedStyle(document.body).backgroundColor || "#f6f4ef"}`;
  wrapper.appendChild(clone);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
    `<foreignObject width="100%" height="100%">${new XMLSerializer().serializeToString(wrapper)}</foreignObject></svg>`;
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
  try {
    const img = await loadImage(url);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas");
    ctx.scale(dpr, dpr);
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

function copyTree(source: Element, clone: Element): void {
  const cs = getComputedStyle(source);
  let css = "";
  for (let i = 0; i < cs.length; i += 1) {
    const prop = cs.item(i);
    css += `${prop}:${cs.getPropertyValue(prop)};`;
  }
  clone.setAttribute("style", css);
  if (source instanceof HTMLInputElement && clone instanceof HTMLInputElement) {
    clone.value = source.value;
    clone.checked = source.checked;
  } else if (source instanceof HTMLTextAreaElement && clone instanceof HTMLTextAreaElement) {
    clone.value = source.value;
  } else if (source instanceof HTMLSelectElement && clone instanceof HTMLSelectElement) {
    clone.value = source.value;
  }
  const srcKids = source.children;
  const dstKids = clone.children;
  const n = Math.min(srcKids.length, dstKids.length);
  for (let i = 0; i < n; i += 1) copyTree(srcKids[i]!, dstKids[i]!);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image"));
    img.src = src;
  });
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
